// The pure parts of the CCMA and Disputes screen: the vocabularies, and the
// sentences the page leads with.
//
// A PLAIN .ts MODULE WITH NO JSX, for the reason navModel.ts gives: Node's own
// test runner can import it under --experimental-strip-types, which cannot strip
// JSX. The behaviour that is easy to get wrong here is what the page SAYS about a
// date nobody has confirmed, and that is exactly the thing worth a test.

export type DisputeEvent = {
  id: string
  matter_id: string
  matter_ref: number
  forum: string
  case_number: string | null
  event_type: string
  scheduled_on: string
  scheduled_at_time: string | null
  venue: string | null
  source: string
  is_confirmed: boolean
  confirmed_by: string | null
  outcome: string | null
  driver_name: string | null
  applicant_name: string | null
  days_away: number
  calendar_event_id: string | null
  calendar_synced_at: string | null
  calendar_error: string | null
  task_id: string | null
}

export type DisputeMatter = {
  id: string
  ref: number
  forum: string
  case_number: string | null
  matter_type: string | null
  applicant_name: string | null
  respondent_name: string | null
  what_is_claimed: string | null
  status: string
  driver_id: string | null
  driver_link: string
  driver_link_note: string | null
  driver_name: string | null
  driver_status: string | null
  driver_manager: string | null
  received_at: string
  received_by: string
  model_read_at: string | null
  read_error: string | null
  document_count: number
  recommendations_open: number
  events_unconfirmed: number
  next_confirmed_on: string | null
  is_open: boolean
  outcome: string | null
  outcome_note: string | null
  task_ref: number | null
  task_status: string | null
  // Migration 2830. Absent on a Worker deployed before it, hence optional.
  duplicate_of?: string | null
  duplicate_of_ref?: number | null
}

/** A matter withdrawn as filed twice, as getDisputeBoard lists it apart. */
export type DuplicateMatter = {
  id: string
  ref: number
  duplicate_of_ref: number | null
  outcome_note: string | null
  closed_at: string | null
  closed_by: string | null
  document_count: number
}

/**
 * Who may withdraw a matter as a duplicate, mirroring
 * dispute_matter_withdraw_duplicate_v1 so the button is offered only where the
 * database will accept it. Any viewer while the matter is unconfirmed, because
 * filing one needs only view access; the dispute managers once it is Open. The
 * database decides either way; this only avoids offering a button that throws.
 */
export function canWithdrawAsDuplicate(
  m: Pick<DisputeMatter, 'status'>,
  canManage: boolean,
): boolean {
  if (m.status === 'Received' || m.status === 'Awaiting confirmation') return true
  return m.status === 'Open' && canManage
}

/** "#2, a second filing of #1, withdrawn by thabang@fleetly.co.za on Sat 3 Oct 2026." */
export function duplicateLine(d: DuplicateMatter): string {
  const of = d.duplicate_of_ref != null ? `a second filing of #${d.duplicate_of_ref}` : 'a second filing'
  const by = d.closed_by ? `, withdrawn by ${d.closed_by}` : ''
  const on = d.closed_at ? ` on ${formatDate(d.closed_at)}` : ''
  const note = d.outcome_note ? ` ${d.outcome_note}` : ''
  return `#${d.ref}, ${of}${by}${on}.${note}`
}

// Exactly the values dispute_event's check constraint allows. If these two ever
// disagree the screen offers a type the database refuses, which is a save button
// that throws.
export const EVENT_TYPES = [
  'Conciliation',
  'Con-arb',
  'Arbitration',
  'Pre-arbitration',
  'In limine',
  'Rescission',
  'Review',
  'Inspection',
  'Other',
] as const

export const DOC_KINDS = [
  'referral',
  'notice of set down',
  'ruling',
  'award',
  'settlement agreement',
  'correspondence',
  'our submission',
  'unclassified',
  'other',
] as const

export const FORUMS = [
  'CCMA',
  'Bargaining Council',
  'Department of Employment and Labour',
  'Labour Court',
  'Other',
] as const

export const CLOSE_STATUSES = [
  'Settled',
  'Withdrawn',
  'Award for Fleetly',
  'Award against Fleetly',
  'Closed',
] as const

const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

/** 2026-09-24 becomes "Thu 24 Sep 2026". Parsed as UTC so it does not shift. */
export function formatDate(iso: string | null): string {
  if (!iso) return 'no date'
  const d = new Date(iso.slice(0, 10) + 'T00:00:00Z')
  if (Number.isNaN(d.getTime())) return iso
  return `${DAY[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

/**
 * When an event is, in full.
 *
 * A MISSING TIME IS PRINTED AS MISSING, never dropped and never filled in. A
 * set-down notice that gives a date and no time is common, and a screen that
 * silently showed only the date would let somebody assume the time is on file
 * somewhere. CLAUDE.md rule 6: a missing figure shows as missing, never as zero.
 */
export function formatWhen(e: Pick<DisputeEvent, 'scheduled_on' | 'scheduled_at_time'>): string {
  const date = formatDate(e.scheduled_on)
  if (!e.scheduled_at_time) return `${date}, time not stated on the document`
  return `${date}, ${e.scheduled_at_time.slice(0, 5)}`
}

/** "in 12 days", "tomorrow", "today", "8 days ago". */
export function daysAwayLabel(days: number): string {
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days === -1) return 'yesterday'
  if (days > 1) return `in ${days} days`
  return `${Math.abs(days)} days ago`
}

export type Tone = 'green' | 'red' | 'amber' | 'neutral' | 'brand'

/**
 * How an event reads at a glance.
 *
 * AN UNCONFIRMED EVENT IS ALWAYS AMBER, however far away it is, and that
 * overrides everything else. It is not a diary entry, it is a machine's reading
 * of a document waiting for somebody to check it, and colouring it the same as a
 * confirmed date in three weeks would invite somebody to plan around it.
 */
export function eventTone(e: Pick<DisputeEvent, 'is_confirmed' | 'days_away' | 'outcome'>): Tone {
  if (e.outcome) return 'neutral'
  if (!e.is_confirmed) return 'amber'
  if (e.days_away < 0) return 'red'
  if (e.days_away <= 7) return 'red'
  if (e.days_away <= 21) return 'amber'
  return 'green'
}

/** The words beside the colour, so the page is readable without it. */
export function eventStateLabel(
  e: Pick<DisputeEvent, 'is_confirmed' | 'days_away' | 'outcome'>,
): string {
  if (e.outcome) return 'outcome recorded'
  if (!e.is_confirmed) return 'read from the document, not yet confirmed'
  if (e.days_away < 0) return 'the date has passed with no outcome recorded'
  return daysAwayLabel(e.days_away)
}

/**
 * The one line at the top of the page: when is the next one, who is it for, what
 * is it about.
 *
 * CONFIRMED ONLY. An unconfirmed date is never the answer to "when is the next
 * one", because nobody has agreed that it is a date. When the only thing on file
 * is unconfirmed the sentence says exactly that instead, which is the honest
 * answer and also the one that gets somebody to open the document.
 */
export function nextEventSentence(events: DisputeEvent[]): {
  headline: string
  detail: string | null
  tone: Tone
} {
  const live = events.filter((e) => !e.outcome)
  const confirmed = live
    .filter((e) => e.is_confirmed && e.days_away >= 0)
    .sort((a, b) => a.scheduled_on.localeCompare(b.scheduled_on))

  if (confirmed.length > 0) {
    const e = confirmed[0]
    const who = e.driver_name ?? e.applicant_name ?? 'applicant not yet named'
    return {
      headline: `${e.event_type} ${daysAwayLabel(e.days_away)}, ${formatWhen(e)}`,
      detail: `${who}. ${e.forum} matter #${e.matter_ref}${e.case_number ? `, case ${e.case_number}` : ', case number not on file'}.${e.venue ? ` ${e.venue}.` : ''}`,
      tone: eventTone(e),
    }
  }

  const overdue = live.filter((e) => e.is_confirmed && e.days_away < 0)
  if (overdue.length > 0) {
    return {
      headline: `${overdue.length} confirmed ${overdue.length === 1 ? 'date has' : 'dates have'} passed with no outcome recorded`,
      detail: 'Record what happened on each, or postpone it. Until then nobody can tell from this screen whether Fleetly appeared.',
      tone: 'red',
    }
  }

  const unconfirmed = live.filter((e) => !e.is_confirmed)
  if (unconfirmed.length > 0) {
    return {
      headline: 'No confirmed date in the diary',
      detail: `${unconfirmed.length} ${unconfirmed.length === 1 ? 'date was' : 'dates were'} read off a document and ${unconfirmed.length === 1 ? 'is' : 'are'} waiting for somebody to check ${unconfirmed.length === 1 ? 'it' : 'them'} against the paper. Until that happens ${unconfirmed.length === 1 ? 'it is' : 'they are'} not in anybody's diary.`,
      tone: 'amber',
    }
  }

  return {
    headline: 'Nothing in the diary',
    detail: 'No confirmed hearing on any open matter.',
    tone: 'neutral',
  }
}

/** What a matter is called in a list. */
export function matterHeadline(m: DisputeMatter): string {
  const who = m.driver_name ?? m.applicant_name
  if (who && m.case_number) return `${who}, ${m.case_number}`
  if (who) return `${who}, case number not on file`
  if (m.case_number) return `${m.case_number}, applicant not yet named`
  return `${m.forum} matter #${m.ref}, nothing confirmed yet`
}

/**
 * What the matter needs from a person next, in one phrase, or null when it is
 * waiting on nobody.
 *
 * ORDERED BY WHAT COSTS MOST IF IT IS MISSED. A confirmed date that has passed
 * with no outcome outranks an unread document, because the first means nobody
 * can tell whether Fleetly turned up.
 */
export function matterNeeds(m: DisputeMatter): string | null {
  if (!m.is_open) return null
  if (m.document_count === 0) return 'upload the document'
  if (m.read_error) return 'the document could not be read, so read it yourself'
  if (m.status === 'Received') return 'read the document'
  if (m.events_unconfirmed > 0) return `confirm ${m.events_unconfirmed === 1 ? 'the date' : `${m.events_unconfirmed} dates`}`
  if (m.status === 'Awaiting confirmation') return 'confirm who this is about'
  if (m.driver_link === 'unlinked' || m.driver_link === 'proposed') return 'confirm who this is about'
  if (m.recommendations_open > 0) {
    return `decide ${m.recommendations_open} recommendation${m.recommendations_open === 1 ? '' : 's'}`
  }
  if (!m.next_confirmed_on) return 'no confirmed date on file'
  return null
}
